import React from 'react';

interface FuturisticLoaderProps {
  size?: number;
  className?: string;
}

const FuturisticLoaderInner: React.FC<FuturisticLoaderProps> = ({ 
  size = 80, 
  className = "" 
}) => {
  const primaryColor = "#ecb22d";
  const secondaryColor = "#fbbf24";
  const accentColor = "#f59e0b";
  const glowColor = "#fef3c7";

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        className="overflow-visible"
      >
        {/* Enhanced gradient definitions for lava effect */}
        <defs>
          {/* Main lava gradient with multiple stops */}
          <radialGradient id="lavaGradient" cx="0.2" cy="0.2" r="1.2">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="15%" stopColor={glowColor} stopOpacity="0.8" />
            <stop offset="35%" stopColor={secondaryColor} stopOpacity="0.9" />
            <stop offset="60%" stopColor={primaryColor} stopOpacity="1" />
            <stop offset="85%" stopColor={accentColor} stopOpacity="0.8" />
            <stop offset="100%" stopColor="#d97706" stopOpacity="0.6" />
          </radialGradient>
          
          {/* Animated gradient for rolling effect */}
          <radialGradient id="rollingGradient" cx="0.4" cy="0.4" r="0.8">
            <stop offset="0%" stopColor={glowColor} stopOpacity="1" />
            <stop offset="30%" stopColor={secondaryColor} stopOpacity="0.9" />
            <stop offset="70%" stopColor={primaryColor} stopOpacity="0.7" />
            <stop offset="100%" stopColor={accentColor} stopOpacity="0.5" />
            <animateTransform
              attributeName="gradientTransform"
              type="rotate"
              values="0 60 60;360 60 60"
              dur="3s"
              repeatCount="indefinite"
            />
          </radialGradient>
          
          {/* Inner core gradient */}
          <radialGradient id="coreGradient" cx="0.3" cy="0.3" r="0.7">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="40%" stopColor={glowColor} stopOpacity="0.8" />
            <stop offset="100%" stopColor={secondaryColor} stopOpacity="0.6" />
          </radialGradient>
          
          {/* Glow filter for energy effect */}
          <filter id="energyGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          {/* Morphing filter */}
          <filter id="morphing" x="-20%" y="-20%" width="140%" height="140%">
            <feTurbulence baseFrequency="0.1" numOctaves="2" result="noise">
              <animate attributeName="baseFrequency" 
                values="0.1;0.3;0.1" 
                dur="4s" 
                repeatCount="indefinite"/>
            </feTurbulence>
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="2"/>
          </filter>
        </defs>

        {/* Outer energy ring with pulse */}
        <circle
          cx="60"
          cy="60"
          r="55"
          fill="none"
          stroke={primaryColor}
          strokeWidth="0.8"
          opacity="0.2"
        >
          <animate attributeName="r" values="50;60;50" dur="3s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.1;0.4;0.1" dur="3s" repeatCount="indefinite"/>
        </circle>

        {/* Secondary ring */}
        <circle
          cx="60"
          cy="60"
          r="45"
          fill="none"
          stroke={secondaryColor}
          strokeWidth="0.5"
          opacity="0.3"
        >
          <animate attributeName="r" values="42;48;42" dur="2s" repeatCount="indefinite"/>
        </circle>

        {/* Main morphing lava ball */}
        <circle
          cx="60"
          cy="60"
          r="32"
          fill="url(#lavaGradient)"
          filter="url(#energyGlow)"
        >
          <animate attributeName="r" values="28;35;28" dur="2.5s" repeatCount="indefinite"/>
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="0 60 60;360 60 60"
            dur="4s"
            repeatCount="indefinite"
          />
        </circle>
        
        {/* Rolling inner layer */}
        <circle
          cx="60"
          cy="60"
          r="25"
          fill="url(#rollingGradient)"
          opacity="0.8"
          filter="url(#morphing)"
        >
          <animate attributeName="r" values="22;28;22" dur="3s" repeatCount="indefinite"/>
        </circle>

        {/* Dynamic lava bubbles */}
        <g>
          <circle cx="45" cy="40" r="4" fill={secondaryColor} opacity="0.7">
            <animate attributeName="r" values="2;6;2" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.4;0.9;0.4" dur="2s" repeatCount="indefinite"/>
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 60;360 60 60"
              dur="6s"
              repeatCount="indefinite"
            />
          </circle>
          
          <circle cx="75" cy="50" r="3" fill={accentColor} opacity="0.8">
            <animate attributeName="r" values="1;5;1" dur="1.8s" repeatCount="indefinite"/>
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 60;-360 60 60"
              dur="5s"
              repeatCount="indefinite"
            />
          </circle>
          
          <circle cx="50" cy="80" r="3.5" fill={primaryColor} opacity="0.6">
            <animate attributeName="r" values="2;5;2" dur="2.2s" repeatCount="indefinite"/>
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 60;360 60 60"
              dur="7s"
              repeatCount="indefinite"
            />
          </circle>
        </g>

        {/* Energy particles orbiting */}
        <g>
          <circle cx="35" cy="60" r="2" fill={glowColor} opacity="0.9">
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 60;360 60 60"
              dur="4s"
              repeatCount="indefinite"
            />
          </circle>
          
          <circle cx="85" cy="60" r="1.5" fill={secondaryColor} opacity="0.8">
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 60;-360 60 60"
              dur="3s"
              repeatCount="indefinite"
            />
          </circle>
          
          <circle cx="60" cy="35" r="1.8" fill={accentColor} opacity="0.7">
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 60;360 60 60"
              dur="5s"
              repeatCount="indefinite"
            />
          </circle>
          
          <circle cx="60" cy="85" r="1.3" fill={primaryColor} opacity="0.6">
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 60 60;-360 60 60"
              dur="6s"
              repeatCount="indefinite"
            />
          </circle>
        </g>

        {/* Central energy core with intense glow */}
        <circle
          cx="60"
          cy="60"
          r="12"
          fill="url(#coreGradient)"
          filter="url(#energyGlow)"
        >
          <animate attributeName="r" values="8;15;8" dur="1.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.8;1;0.8" dur="1.5s" repeatCount="indefinite"/>
        </circle>
        
        {/* Ultra-bright center point */}
        <circle
          cx="60"
          cy="60"
          r="4"
          fill="#ffffff"
          opacity="0.9"
        >
          <animate attributeName="r" values="2;6;2" dur="1s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.6;1;0.6" dur="1s" repeatCount="indefinite"/>
        </circle>
      </svg>
    </div>
  );
};

export const FuturisticLoader = React.memo(FuturisticLoaderInner); 